import type { LiniyaApi } from '@liniya/api-client';

/**
 * The one configured client, supplied by whichever app is running.
 *
 * The two clients build their `LiniyaApi` differently — the browser keeps the
 * refresh credential in a cookie it cannot read, the phone keeps it in the
 * platform keystore — so this package cannot construct one. It is handed the
 * finished instance at startup instead.
 *
 * Access goes through a proxy so that the hooks below can write `api.chats`
 * exactly as they did when they lived in the web client. The alternative,
 * threading the instance through every call site, would change a hundred lines
 * of working code to express the same thing.
 */
let injected: LiniyaApi | null = null;
let subscribeToAuthenticationLost: AuthenticationLostSubscription | null = null;

/**
 * Register a listener for the session becoming unrecoverable.
 *
 * Supplied alongside the client because only the code that constructed it
 * knows how it was wired: the handler is a constructor option, not something
 * that can be attached afterwards.
 */
export type AuthenticationLostSubscription = (handler: () => void) => () => void;

export function provideApi(
  instance: LiniyaApi,
  onAuthenticationLost?: AuthenticationLostSubscription,
): void {
  injected = instance;
  subscribeToAuthenticationLost = onAuthenticationLost ?? null;
}

export function onAuthenticationLost(handler: () => void): () => void {
  return subscribeToAuthenticationLost?.(handler) ?? (() => undefined);
}

function required(): LiniyaApi {
  if (!injected) {
    throw new Error(
      '@liniya/client-core: до первого запроса нужно вызвать provideApi() с настроенным клиентом.',
    );
  }
  return injected;
}

export const api = new Proxy({} as LiniyaApi, {
  get: (_target, property) => required()[property as keyof LiniyaApi],
});

/**
 * A token that is certainly still valid, refreshing first if it is not.
 *
 * The WebSocket carries its credential in the handshake and cannot be given a
 * new one afterwards, so a token that expires mid-connection is a connection
 * that dies. Thirty seconds of margin covers a slow handshake.
 */
export async function currentAccessToken(): Promise<string | null> {
  const tokens = await api.http.currentTokens();
  if (!tokens) return null;
  if (tokens.accessExpiresAt - 30_000 > Date.now()) return tokens.accessToken;
  const refreshed = await api.http.refresh();
  return refreshed?.accessToken ?? null;
}
